import axios from "axios";

export const api = axios.create({
  timeout: 20000,
});

/** Full Google Sheets rebuild (many tabs + formatting) can exceed the default 20s. */
export const GOOGLE_SHEETS_SYNC_TIMEOUT_MS = 180000;

api.interceptors.request.use((config) => {
  const isClient = typeof window !== "undefined";
  
  config.baseURL = isClient
    ? "/api"
    : (process.env.NEXT_PUBLIC_API_URL || "http://127.0.0.1:3008");

  if (isClient) {
    const { getCookie } = require("./cookies");
    const token = getCookie("access_token");
    if (token && token !== "undefined" && token !== "null") {
        if (!config.headers) config.headers = {};
        config.headers.Authorization = `Bearer ${token}`;
    }
  }
  
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const data = error?.response?.data;
    const reqUrl = originalRequest?.url || "";

    // Handle 401 Unauthorized - Attempt to refresh token
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const { getCookie, setCookie, deleteCookie } = require("./cookies");
        const refreshToken = getCookie("refresh_token");
        
        if (refreshToken) {
          // Use axios instead of 'api' to avoid loops
          const axiosLib = require("axios");
          const isClient = typeof window !== "undefined";
          const baseURL = isClient ? "/api" : (process.env.NEXT_PUBLIC_API_URL || "http://127.0.0.1:3008");

          const response = await axiosLib.post(`${baseURL}/refresh-token`, { 
            refresh_token: refreshToken 
          });

          const newData = response?.data?.data;
          if (newData?.access_token) {
            setCookie("access_token", newData.access_token);
            setCookie("refresh_token", newData.refresh_token);
            
            // Retry the original request with the new token
            originalRequest.headers.Authorization = `Bearer ${newData.access_token}`;
            return axiosLib(originalRequest);
          }
        }
      } catch (refreshError) {
        // Refresh failed, logout if on client
        if (typeof window !== "undefined") {
          const { deleteCookie } = require("./cookies");
          deleteCookie("access_token");
          deleteCookie("refresh_token");
          deleteCookie("role");
          // Avoid hard reloads (can look like app "closing" in PWA/webview).
          // Let the React auth layer handle navigation.
          window.dispatchEvent(new CustomEvent("auth:force-logout"));
        }
      }
    }

    // Handle different error formats from backend
    let message = "Request failed";
    
    if (status === 401) {
      // Prefer backend-provided message (e.g. login "Invalid credentials")
      const backendMsg = data?.message || (typeof data?.detail === "string" ? data.detail : data?.detail?.message);
      const isLogin = reqUrl.endsWith("/login") || reqUrl.includes("/login?");
      const isRefresh = reqUrl.endsWith("/refresh-token") || reqUrl.includes("/refresh-token?");

      if (isLogin) {
        message = backendMsg || "Check your email and password.";
      } else if (isRefresh) {
        message = "Session expired. Please log in again.";
      } else {
        message = backendMsg || "Session expired. Please log in again.";
      }
    } else if (data?.detail) {
      if (typeof data.detail === 'string') {
        message = data.detail;
      } else if (data.detail?.message) {
        message = data.detail.message;
      }
    } else if (data?.message) {
      message = data.message;
    } else if (error.code === "ECONNABORTED") {
      const isSheets =
        /google.?sheets|sheets\/(sync|prepare)/i.test(reqUrl) ||
        Boolean(originalRequest?.__googleSheets);
      message = isSheets
        ? "Request timed out. The server may still be updating Google Sheets — check the spreadsheet in a minute."
        : /\/ai\/summaries/.test(reqUrl)
          ? "Summary generation timed out. Please try again."
          : reqUrl.includes("/ai/")
            ? "SamAI timed out. Please try again."
            : "Request timed out. Please try again.";
    } else if (error.code === "ECONNRESET" || error.code === "ERR_NETWORK" || !error.response) {
      message = /\/ai\/summaries/.test(reqUrl)
        ? "Summary connection dropped. Please try again."
        : reqUrl.includes("/ai/")
          ? "SamAI connection dropped. Please try again."
          : "Network error. Please try again.";
    }
    
    const normalized = new Error(message);
    normalized.code = error.code;
    normalized.status = status;
    normalized.details = data?.details || data?.detail?.details;
    normalized.response = error.response;
    
    throw normalized;
  }
);

/** AI Attendance Insights helpers (backend /ai/*). */
export const AI_ASK_TIMEOUT_MS = 120000;

export const aiApi = {
  dashboard: (days = 30, year, month) => {
    const q = new URLSearchParams({ days: String(days) });
    if (year) q.set("year", String(year));
    if (month) q.set("month", String(month));
    return api.get(`/ai/dashboard?${q}`);
  },
  me: (days = 30, year, month) => {
    const q = new URLSearchParams({ days: String(days) });
    if (year) q.set("year", String(year));
    if (month) q.set("month", String(month));
    return api.get(`/ai/me?${q}`);
  },
  ask: (question, conversationId, year, month, employeeId, contextEmployeeId, contextIntent) =>
    api.post(
      "/ai/ask",
      {
        question,
        conversation_id: conversationId || undefined,
        year: year || undefined,
        month: month || undefined,
        employee_id: employeeId || undefined,
        context_employee_id: contextEmployeeId || undefined,
        context_intent: contextIntent || undefined,
      },
      { timeout: AI_ASK_TIMEOUT_MS }
    ),
  scores: (days = 30) => api.get(`/ai/employees/scores?days=${days}`),
  forecast: () => api.get("/ai/forecast"),
  trends: (days = 30) => api.get(`/ai/trends?days=${days}`),
  anomalies: (days = 30) => api.get(`/ai/anomalies?days=${days}`),
  leaveInsights: () => api.get("/ai/leave-insights"),
  departments: (days = 30) => api.get(`/ai/departments?days=${days}`),
  summaryLatest: (periodType = "weekly") =>
    api.get(`/ai/summaries/latest?period_type=${periodType}`),
  summaryGenerate: (periodType = "weekly") =>
    api.post(
      "/ai/summaries/generate",
      { period_type: periodType },
      { timeout: AI_ASK_TIMEOUT_MS }
    ),
  status: () => api.get("/ai/status"),
  deleteConversation: (id) => api.delete(`/ai/conversations/${id}`),
  truncateConversation: (id, messageId) =>
    api.post(`/ai/conversations/${id}/truncate`, { message_id: messageId }),
  sendMail: (payload) => api.post("/ai/mail/send", payload, { timeout: 60000 }),
  validateLeave: (payload) => api.post("/ai/leave/validate", payload),
  submitLeave: (payload) => api.post("/ai/leave/submit", payload),
};
